import React, { useState } from 'react';
import { UserApi, EmailUpdateRequest } from '../api/UserApi';

interface EmailUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  currentEmail?: string;
  isOwnProfile: boolean;
}

const EmailUpdateModal: React.FC<EmailUpdateModalProps> = ({
  isOpen,
  onClose,
  userId,
  currentEmail = '',
  isOwnProfile
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!newEmail) {
        setError('Please enter a new email address');
        return;
      }
      
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
        setError('Please enter a valid email address');
        return;
      }

      if (newEmail === currentEmail) {
        setError('New email must be different from current email');
        return;
      }

      const request: EmailUpdateRequest = {
        newEmail
      };
      
      await UserApi.updateEmail(userId, request);
      setSuccess('Email updated successfully');
      
      // Reset form and close modal after success
      setTimeout(() => {
        setNewEmail('');
        onClose();
        setSuccess('');
        // Refresh the page to show updated email
        window.location.reload();
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data || 'An error occurred while updating email');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewEmail('');
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {isOwnProfile ? 'Update Your Email' : 'Update User Email'}
        </h2>

        {currentEmail && (
          <div className="mb-4 p-3 bg-gray-100 rounded">
            <p className="text-sm text-gray-600">
              Current email: <strong>{currentEmail}</strong>
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Email Address
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter new email address"
              required
            />
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailUpdateModal;
